import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { getTrucksForSaleTableColumns } from "../../../data/FormColumns/Sale/trucksForSaleTableColumns";
import PreviewGalleryModal from "../PreviewGalleryModal/PreviewGalleryModal";
import FileUploadModal from "../FileUploadModal/FileUploadModal";

const fetchTrucksForSale = async (
  token: string | undefined | null,
  areActive: boolean,
) => {
  const { data } = await axios.get(`${apiVariable}/truckSale/all`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive },
  });
  return data;
};

const TruckSalesTable: React.FC = () => {
  const { token } = useAuth();
  const [areActive, setAreActive] = useState(true);
  const [selectedTruck, setSelectedTruck] = useState<any | null>(null);
  const [fileUploadOpen, setFileUploadOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(
    {},
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["trucksForSale", areActive],
    queryFn: () => fetchTrucksForSale(token, areActive),
    enabled: !!token,
  });

  const filteredTrucksForSale = useMemo(() => {
    if (!data) return [];
    return data.filter((truck: any) =>
      [truck.make, truck.model].some((field) =>
        field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [data, searchTerm]);

  const handleColumnResize = (params: {
    colDef: { field: string };
    width: number;
  }) => {
    setColumnWidths((prev) => ({
      ...prev,
      [params.colDef.field]: params.width,
    }));
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
    </GridToolbarContainer>
  );

  const handleSwitchChange = (event: any) => {
    setAreActive(event.target.checked);
  };

  const handleUpload = (truckRow: any) => {
    setSelectedTruck(truckRow);
    setFileUploadOpen(true);
  };

  const handlePreview = (truckRow: any) => {
    setSelectedTruck(truckRow);
    setPreviewOpen(true);
  };

  const columns = getTrucksForSaleTableColumns(handleUpload, handlePreview);

  return (
    <Box className="incidents-table-container" height={800}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Name or Phone Number"
          sx={{ maxWidth: "100%", width: 400 }}
        />
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={areActive}
            onChange={handleSwitchChange}
            color="primary"
          />
        }
        label={`${areActive ? "Active" : "Inactive"} Sales`}
      />
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Error: {(error as any).message}</Typography>
      ) : (
        <DataGrid
          rows={filteredTrucksForSale}
          columns={columns.map((col) => ({
            ...col,
            width: columnWidths[col.field] || col.width,
          }))}
          onColumnResize={handleColumnResize}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
          slots={{ toolbar: CustomToolbar }}
          sx={{ height: 700 }}
          getRowId={(row) => row.id}
          pagination
        />
      )}

      {selectedTruck && (
        <FileUploadModal
          open={fileUploadOpen}
          onClose={() => setFileUploadOpen(false)}
          truckForSale={selectedTruck}
        />
      )}

      {selectedTruck && (
        <PreviewGalleryModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          truckForSale={selectedTruck}
        />
      )}
    </Box>
  );
};

export default TruckSalesTable;
